/**
 * Users Management Page
 * CRUD operations for users
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import { usersService } from "../services/api/users.service";
import { rolesService } from "../services/api/roles.service";
import { settingsService } from "../services/api/settings.service";
import { workflowService } from "../services/api/workflow.service";
import type {
  ApiUser,
  CreateUserDto,
  UpdateUserDto,
} from "../types/user.types";
import type { Role } from "../types/role.types";
import { WorkflowModule, type Workflow } from "../types/workflow.types";
import BusyOverlay from "../components/common/BusyOverlay";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { REQUIRED_FIELD_MESSAGE } from "../utils/constants";
import { sanitizePhoneNumberInput, validatePhoneNumber } from "../utils/phone";
import { getFullPath } from "../utils/getFullPath";
import { processPassportPhoto } from "../utils/image-processing/passportPhotoHelper";
import "../styles/pages/Users.css";

export const Users: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [availableWorkflows, setAvailableWorkflows] = useState<Workflow[]>([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingEmail] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState<number | "all">("all");
  const menuRef = useRef<HTMLDivElement>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormData, setAddFormData] = useState<CreateUserDto>({
    userNo: "",
    username: "",
    firstName: "",
    middleName: "",
    lastName: "",
    genderId: 1,
    dateOfBirth: "",
    nationalIdNumber: "",
    email: "",
    primaryPhone: "",
    secondaryPhone: "",
    password: "",
    roleId: 0,
    workflowId: undefined,
    isActive: false,
    isConfirmed: false,
    isSystemAdmin: false,
    isClientAdmin: false,
    isUserOnly: true,
    profileImagePath: "",
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");
  const [addValidationErrors, setAddValidationErrors] = useState<
    Record<string, string>
  >({});
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoProcessing, setPhotoProcessing] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateUserDto>({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editValidationErrors, setEditValidationErrors] = useState<
    Record<string, string>
  >({});
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);

  const [maxDateOfBirth, setMaxDateOfBirth] = useState<string>("");

  // Photo processing settings
  const [enableBackgroundRemoval, setEnableBackgroundRemoval] = useState(true);

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("User Management", "Manage system users and permissions");
    fetchData();
    fetchMinimumAge();
  }, [setPageTitle]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        return;
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch workflows for Users module
  useEffect(() => {
    const fetchWorkflowsForModule = async () => {
      //   if (!addFormData.roleId || roles.length === 0) {
      //     setAvailableWorkflows([]);
      //     return;
      //   }

      try {
        setLoadingWorkflows(true);
        const workflows = await workflowService.getWorkflowsByModule(
          WorkflowModule.SystemUsers,
        );

        setAvailableWorkflows(workflows);

        // If only one workflow available, set it as default
        if (workflows.length === 1) {
          setAddFormData((prev) => ({
            ...prev,
            workflowId: workflows[0].id,
          }));
        } else if (workflows.length === 0) {
          setAddFormData((prev) => ({
            ...prev,
            workflowId: undefined,
          }));
        } else {
          // Multiple workflows available, reset selection
          setAddFormData((prev) => ({
            ...prev,
            workflowId: undefined,
          }));
        }
      } catch (err) {
        console.error("Error fetching workflows:", err);
        setAvailableWorkflows([]);
      } finally {
        setLoadingWorkflows(false);
      }
    };

    fetchWorkflowsForModule();
  }, []);

  // Fetch photo processing settings
  useEffect(() => {
    const fetchPhotoSettings = async () => {
      try {
        const setting = await settingsService.getSettingByKey(
          "EnableBackgroundRemoval",
        );
        if (setting && setting.value !== undefined) {
          setEnableBackgroundRemoval(setting.value === "true");
        }
      } catch (err) {
        console.error("Error fetching photo settings:", err);
        // Use default value (true) if fetch fails
        setEnableBackgroundRemoval(true);
      }
    };
    fetchPhotoSettings();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const [usersData, rolesData] = await Promise.all([
        usersService.getUsers(),
        rolesService.getRoles(),
      ]);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMinimumAge = async () => {
    try {
      const setting = await settingsService.getSettingByKey("minimumAge");
      if (setting && setting.value) {
        const age = parseInt(setting.value, 10);
        const today = new Date();
        const maxDate = new Date(
          today.getFullYear() - age,
          today.getMonth(),
          today.getDate(),
        );
        setMaxDateOfBirth(maxDate.toISOString().split("T")[0]);
      }
    } catch (err) {
      console.error("Error fetching minimum age setting:", err);
      const today = new Date();
      const maxDate = new Date(
        today.getFullYear() - 18,
        today.getMonth(),
        today.getDate(),
      );
      setMaxDateOfBirth(maxDate.toISOString().split("T")[0]);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError("");

    const errors: Record<string, string> = {};
    if (!addFormData.firstName.trim())
      errors.firstName = REQUIRED_FIELD_MESSAGE;
    if (!addFormData.lastName.trim()) errors.lastName = REQUIRED_FIELD_MESSAGE;
    if (!addFormData.email.trim()) errors.email = REQUIRED_FIELD_MESSAGE;
    const addPrimaryPhoneError = validatePhoneNumber(addFormData.primaryPhone, {
      required: true,
    });
    if (addPrimaryPhoneError) {
      errors.primaryPhone = addPrimaryPhoneError;
    }
    const addSecondaryPhoneError = validatePhoneNumber(
      addFormData.secondaryPhone,
    );
    if (addSecondaryPhoneError) {
      errors.secondaryPhone = addSecondaryPhoneError;
    }
    if (!addFormData.dateOfBirth) errors.dateOfBirth = REQUIRED_FIELD_MESSAGE;
    if (!addFormData.roleId) errors.roleId = REQUIRED_FIELD_MESSAGE;
    if (!addFormData.workflowId) errors.workflowId = REQUIRED_FIELD_MESSAGE;

    setAddValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setAddLoading(true);
      // Always send as FormData with dto and profileImage
      const payload = {
        userNo: addFormData.userNo,
        username: addFormData.username || addFormData.email,
        firstName: addFormData.firstName,
        middleName: addFormData.middleName,
        lastName: addFormData.lastName,
        genderId: addFormData.genderId,
        dateOfBirth: addFormData.dateOfBirth,
        nationalIdNumber: addFormData.nationalIdNumber,
        email: addFormData.email,
        primaryPhone: addFormData.primaryPhone,
        secondaryPhone: addFormData.secondaryPhone,
        roleId: addFormData.roleId,
        isActive: addFormData.isActive,
        isConfirmed: addFormData.isConfirmed,
        isSystemAdmin: addFormData.isSystemAdmin,
        isClientAdmin: addFormData.isClientAdmin,
        isUserOnly: addFormData.isUserOnly,
        profileImagePath: addFormData.profileImagePath,
      };
      await usersService.createUser(payload, photoFile || undefined);
      setShowAddModal(false);
      resetAddForm();
      await fetchData();
    } catch (err: any) {
      setAddError(err?.message || "Failed to create user");
    } finally {
      setAddLoading(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError("");

    const errors: Record<string, string> = {};
    if (!editFormData.firstName?.trim())
      errors.firstName = REQUIRED_FIELD_MESSAGE;
    if (!editFormData.lastName?.trim())
      errors.lastName = REQUIRED_FIELD_MESSAGE;
    const editPrimaryPhoneError = validatePhoneNumber(
      editFormData.primaryPhone,
      {
        required: true,
      },
    );
    if (editPrimaryPhoneError) {
      errors.primaryPhone = editPrimaryPhoneError;
    }
    const editSecondaryPhoneError = validatePhoneNumber(
      editFormData.secondaryPhone,
    );
    if (editSecondaryPhoneError) {
      errors.secondaryPhone = editSecondaryPhoneError;
    }
    if (!editFormData.dateOfBirth) errors.dateOfBirth = REQUIRED_FIELD_MESSAGE;
    if (!editFormData.roleId) errors.roleId = REQUIRED_FIELD_MESSAGE;

    setEditValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!editUserId) return;

    try {
      setEditLoading(true);
      const payload = {
        id: editUserId,
        ...editFormData,
      };
      await usersService.updateUser(
        editUserId,
        payload,
        editPhotoFile || undefined,
      );
      setShowEditModal(false);
      resetEditForm();
      await fetchData();
    } catch (err: any) {
      setEditError(err?.message || "Failed to update user");
    } finally {
      setEditLoading(false);
    }
  };

  const resetAddForm = () => {
    setAddFormData({
      userNo: "",
      username: "",
      firstName: "",
      middleName: "",
      lastName: "",
      genderId: 1,
      dateOfBirth: "",
      nationalIdNumber: "",
      email: "",
      primaryPhone: "",
      secondaryPhone: "",
      password: "",
      roleId: 0,
      workflowId: undefined,
      isActive: true,
      isConfirmed: true,
      isSystemAdmin: false,
      isClientAdmin: false,
      isUserOnly: true,
      profileImagePath: "",
    });
    setAddError("");
    setAddValidationErrors({});
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const resetEditForm = () => {
    setEditFormData({});
    setEditUserId(null);
    setEditError("");
    setEditValidationErrors({});
    setEditPhotoFile(null);
    setEditPhotoPreview(null);
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setAddError("Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setAddError("Image size must be less than 5MB");
        return;
      }

      try {
        setPhotoProcessing(true);
        setAddError("");
        const result = await processPassportPhoto(
          file,
          enableBackgroundRemoval,
        );
        setPhotoFile(result.file);
        setPhotoPreview(result.base64);
      } catch (err: any) {
        setAddError(err?.message || "Failed to process photo");
      } finally {
        setPhotoProcessing(false);
      }
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setAddFormData({ ...addFormData, profileImagePath: "" });
  };

  const handleEditPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setEditError("Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setEditError("Image size must be less than 5MB");
        return;
      }

      setEditPhotoFile(file);
      setEditFormData((prev) => ({
        ...prev,
        profileImagePath: "",
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeEditPhoto = () => {
    setEditPhotoFile(null);
    setEditPhotoPreview(null);
    setEditFormData({ ...editFormData, profileImagePath: "" });
  };

  const getRoleName = (roleId: number): string => {
    const role = roles.find((r) => r.id === roleId);
    return role?.name || `Role ${roleId}`;
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === "all" || user.roleId === roleFilter;

    return matchesSearch && matchesRole;
  });

  if (!HasPermission(ModuleId.Users, ApplicationPermissionId.View)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>You do not have permission to view users</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="users-page">
          <BusyOverlay
            isVisible={
              loading ||
              addLoading ||
              editLoading ||
              sendingEmail ||
              photoProcessing
            }
            message={
              photoProcessing
                ? "Processing photo — removing background & resizing..."
                : sendingEmail
                  ? "Sending password creation link..."
                  : addLoading
                    ? "Creating user..."
                    : editLoading
                      ? "Updating user..."
                      : "Loading users..."
            }
          />

          <div className="users-content">
            <div className="users-header">
              <input
                type="text"
                className="search-input"
                placeholder="Search users by name, username, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select
                className="role-filter"
                value={roleFilter}
                onChange={(e) =>
                  setRoleFilter(
                    e.target.value === "all"
                      ? "all"
                      : parseInt(e.target.value, 10),
                  )
                }
              >
                <option value="all">All Roles</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              {HasPermission(
                ModuleId.Users,
                ApplicationPermissionId.Create,
              ) && (
                <button
                  className="btn btn--primary"
                  onClick={() => setShowAddModal(true)}
                >
                  <span>➕</span> Add New User
                </button>
              )}
            </div>

            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
                <button onClick={() => setError("")} className="alert-close">
                  ✕
                </button>
              </div>
            )}

            <div className="users-table-container">
              {filteredUsers.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state__icon">👥</div>
                  <p className="empty-state__text">
                    {searchTerm || roleFilter !== "all"
                      ? "No users found matching your criteria"
                      : "No users yet"}
                  </p>
                </div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Photo</th>
                      <th>UID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="user-avatar">
                            {user.profileImagePath ? (
                              <img
                                src={
                                  getFullPath(user.profileImagePath) ||
                                  undefined
                                }
                                alt={`${user.firstName} ${user.lastName}`}
                                className="user-avatar-img"
                              />
                            ) : (
                              <div className="user-avatar-initials">
                                {user.firstName?.charAt(0)}
                                {user.lastName?.charAt(0)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{(user as any).uid || user.userNo || user.id}</td>
                        <td className="user-name">
                          {user.firstName}{" "}
                          {user.middleName ? `${user.middleName} ` : ""}
                          {user.lastName}
                        </td>
                        <td>{user.email}</td>
                        <td>{user.primaryPhone}</td>
                        <td>{getRoleName(user.roleId)}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              user.isActive
                                ? "status-badge--active"
                                : "status-badge--inactive"
                            }`}
                          >
                            {user.status?.name ||
                              user.statusName ||
                              (user.isActive ? "Active" : "Inactive")}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn-icon view-detail-icon"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            title="View user details"
                          >
                            👁️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {showAddModal && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">Add New User</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowAddModal(false);
                      resetAddForm();
                    }}
                    disabled={addLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleCreateUser}>
                  {addError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{addError}</span>
                      <button
                        type="button"
                        onClick={() => setAddError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div
                    className="form-group"
                    style={{ marginBottom: "var(--spacing-lg)" }}
                  >
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "var(--text-secondary, #6b7280)",
                        marginBottom: "0.5rem",
                        textAlign: "left",
                      }}
                    >
                      Profile Photo
                    </label>
                    <div className="photo-upload-container">
                      {photoPreview ? (
                        <div className="photo-preview">
                          <img
                            src={photoPreview || undefined}
                            alt="Preview"
                            className="preview-image"
                          />
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            onClick={removePhoto}
                          >
                            Remove Photo
                          </button>
                        </div>
                      ) : (
                        <div className="photo-upload-area">
                          <input
                            type="file"
                            id="photoUpload"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor="photoUpload"
                            className="photo-upload-label"
                          >
                            <div className="upload-icon">📷</div>
                            <div className="upload-text">
                              <span className="upload-title">
                                Click to upload photo
                              </span>
                              <span className="upload-subtitle">
                                PNG, JPG up to 5MB
                              </span>
                            </div>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-grid-responsive">
                    <div
                      className={`form-group ${addFormData.firstName ? "has-value" : ""} ${addValidationErrors.firstName ? "form-group--error" : ""}`}
                    >
                      <input
                        type="text"
                        className={`form-input ${addValidationErrors.firstName ? "form-input--error" : ""}`}
                        value={addFormData.firstName}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            firstName: e.target.value,
                          });
                          if (addValidationErrors.firstName) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.firstName;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        First Name <span className="required">*</span>
                      </label>
                      {addValidationErrors.firstName && (
                        <div className="field-error">
                          {addValidationErrors.firstName}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${addFormData.middleName ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={addFormData.middleName}
                        onChange={(e) =>
                          setAddFormData({
                            ...addFormData,
                            middleName: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">Middle Name</label>
                    </div>

                    <div
                      className={`form-group ${addFormData.lastName ? "has-value" : ""} ${addValidationErrors.lastName ? "form-group--error" : ""}`}
                    >
                      <input
                        type="text"
                        className={`form-input ${addValidationErrors.lastName ? "form-input--error" : ""}`}
                        value={addFormData.lastName}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            lastName: e.target.value,
                          });
                          if (addValidationErrors.lastName) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.lastName;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Last Name <span className="required">*</span>
                      </label>
                      {addValidationErrors.lastName && (
                        <div className="field-error">
                          {addValidationErrors.lastName}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${addFormData.email ? "has-value" : ""} ${addValidationErrors.email ? "form-group--error" : ""}`}
                    >
                      <input
                        type="email"
                        className={`form-input ${addValidationErrors.email ? "form-input--error" : ""}`}
                        value={addFormData.email}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            email: e.target.value,
                          });
                          if (addValidationErrors.email) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.email;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Email <span className="required">*</span>
                      </label>
                      {addValidationErrors.email && (
                        <div className="field-error">
                          {addValidationErrors.email}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${addFormData.primaryPhone ? "has-value" : ""} ${addValidationErrors.primaryPhone ? "form-group--error" : ""}`}
                    >
                      <input
                        type="tel"
                        className={`form-input ${addValidationErrors.primaryPhone ? "form-input--error" : ""}`}
                        value={addFormData.primaryPhone}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            primaryPhone: sanitizePhoneNumberInput(
                              e.target.value,
                            ),
                          });
                          if (addValidationErrors.primaryPhone) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.primaryPhone;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Primary Phone <span className="required">*</span>
                      </label>
                      {addValidationErrors.primaryPhone && (
                        <div className="field-error">
                          {addValidationErrors.primaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${addFormData.secondaryPhone ? "has-value" : ""} ${addValidationErrors.secondaryPhone ? "form-group--error" : ""}`}
                    >
                      <input
                        type="tel"
                        className={`form-input ${addValidationErrors.secondaryPhone ? "form-input--error" : ""}`}
                        value={addFormData.secondaryPhone}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            secondaryPhone: sanitizePhoneNumberInput(
                              e.target.value,
                            ),
                          });
                          if (addValidationErrors.secondaryPhone) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.secondaryPhone;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                      />
                      <label className="form-label">Secondary Phone</label>
                      {addValidationErrors.secondaryPhone && (
                        <div className="field-error">
                          {addValidationErrors.secondaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${addFormData.nationalIdNumber ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={addFormData.nationalIdNumber}
                        onChange={(e) =>
                          setAddFormData({
                            ...addFormData,
                            nationalIdNumber: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">National ID</label>
                    </div>

                    <div
                      className={`form-group ${addFormData.dateOfBirth ? "has-value" : ""} ${addValidationErrors.dateOfBirth ? "form-group--error" : ""}`}
                    >
                      <input
                        type="date"
                        className={`form-input ${addValidationErrors.dateOfBirth ? "form-input--error" : ""}`}
                        value={addFormData.dateOfBirth}
                        max={maxDateOfBirth}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            dateOfBirth: e.target.value,
                          });
                          if (addValidationErrors.dateOfBirth) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.dateOfBirth;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Date of Birth <span className="required">*</span>
                      </label>
                      {addValidationErrors.dateOfBirth && (
                        <div className="field-error">
                          {addValidationErrors.dateOfBirth}
                        </div>
                      )}
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={addFormData.genderId}
                        onChange={(e) =>
                          setAddFormData({
                            ...addFormData,
                            genderId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                      >
                        <option value={1}>Male</option>
                        <option value={2}>Female</option>
                      </select>
                      <label className="form-label">
                        Gender <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${addFormData.roleId ? "has-value" : ""} ${addValidationErrors.roleId ? "form-group--error" : ""}`}
                    >
                      <select
                        className={`form-input ${addValidationErrors.roleId ? "form-input--error" : ""}`}
                        value={addFormData.roleId || ""}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            roleId: parseInt(e.target.value, 10),
                          });
                          if (addValidationErrors.roleId) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.roleId;
                              return newErrors;
                            });
                          }
                        }}
                        required
                      >
                        <option value="">Select Role</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">
                        Role <span className="required">*</span>
                      </label>
                      {addValidationErrors.roleId && (
                        <div className="field-error">
                          {addValidationErrors.roleId}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${addFormData.workflowId ? "has-value" : ""} ${addValidationErrors.workflowId ? "form-group--error" : ""}`}
                    >
                      <select
                        className={`form-input ${addValidationErrors.workflowId ? "form-input--error" : ""}`}
                        value={addFormData.workflowId || ""}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            workflowId: e.target.value
                              ? parseInt(e.target.value, 10)
                              : undefined,
                          });
                          if (addValidationErrors.workflowId) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.workflowId;
                              return newErrors;
                            });
                          }
                        }}
                        required
                      >
                        <option value="">
                          {loadingWorkflows
                            ? "Loading workflows..."
                            : "Select Workflow"}
                        </option>
                        {availableWorkflows.map((workflow) => (
                          <option key={workflow.id} value={workflow.id}>
                            {workflow.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">
                        Workflow <span className="required">*</span>
                      </label>
                      {addValidationErrors.workflowId && (
                        <div className="field-error">
                          {addValidationErrors.workflowId}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowAddModal(false);
                        resetAddForm();
                      }}
                      disabled={addLoading}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={addLoading}
                    >
                      {addLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Creating...
                        </>
                      ) : (
                        "Create User"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showEditModal && editUserId && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">Edit User</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowEditModal(false);
                      resetEditForm();
                    }}
                    disabled={editLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleUpdateUser}>
                  {editError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{editError}</span>
                      <button
                        type="button"
                        onClick={() => setEditError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div
                    className="form-group"
                    style={{ marginBottom: "var(--spacing-lg)" }}
                  >
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "var(--text-secondary, #6b7280)",
                        marginBottom: "0.5rem",
                        textAlign: "left",
                      }}
                    >
                      Profile Photo
                    </label>
                    <div className="photo-upload-container">
                      {editPhotoPreview ? (
                        <div className="photo-preview">
                          <img
                            src={editPhotoPreview || undefined}
                            alt="Preview"
                            className="preview-image"
                          />
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            onClick={removeEditPhoto}
                          >
                            Remove Photo
                          </button>
                        </div>
                      ) : editFormData.profileImagePath ? (
                        <div className="photo-preview">
                          <img
                            src={
                              getFullPath(editFormData.profileImagePath) ||
                              undefined
                            }
                            alt="Current"
                            className="preview-image"
                          />
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            onClick={removeEditPhoto}
                          >
                            Remove Photo
                          </button>
                        </div>
                      ) : (
                        <div className="photo-upload-area">
                          <input
                            type="file"
                            id="editPhotoUpload"
                            accept="image/*"
                            onChange={handleEditPhotoChange}
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor="editPhotoUpload"
                            className="photo-upload-label"
                          >
                            <div className="upload-icon">📷</div>
                            <div className="upload-text">
                              <span className="upload-title">
                                Click to upload photo
                              </span>
                              <span className="upload-subtitle">
                                PNG, JPG up to 5MB
                              </span>
                            </div>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-grid-responsive">
                    <div className="form-group has-value">
                      <input
                        type="text"
                        className="form-input"
                        value={
                          (editFormData as any).uid || editFormData.userNo || ""
                        }
                        disabled
                        placeholder=" "
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      />
                      <label className="form-label">UID</label>
                    </div>

                    <div
                      className={`form-group ${editFormData.firstName ? "has-value" : ""} ${editValidationErrors.firstName ? "form-group--error" : ""}`}
                    >
                      <input
                        type="text"
                        className={`form-input ${editValidationErrors.firstName ? "form-input--error" : ""}`}
                        value={editFormData.firstName || ""}
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            firstName: e.target.value,
                          });
                          if (editValidationErrors.firstName) {
                            setEditValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.firstName;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        First Name <span className="required">*</span>
                      </label>
                      {editValidationErrors.firstName && (
                        <div className="field-error">
                          {editValidationErrors.firstName}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${editFormData.middleName ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editFormData.middleName || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            middleName: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">Middle Name</label>
                    </div>

                    <div
                      className={`form-group ${editFormData.lastName ? "has-value" : ""} ${editValidationErrors.lastName ? "form-group--error" : ""}`}
                    >
                      <input
                        type="text"
                        className={`form-input ${editValidationErrors.lastName ? "form-input--error" : ""}`}
                        value={editFormData.lastName || ""}
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            lastName: e.target.value,
                          });
                          if (editValidationErrors.lastName) {
                            setEditValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.lastName;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Last Name <span className="required">*</span>
                      </label>
                      {editValidationErrors.lastName && (
                        <div className="field-error">
                          {editValidationErrors.lastName}
                        </div>
                      )}
                    </div>

                    <div className="form-group has-value">
                      <input
                        type="email"
                        className="form-input"
                        value={editFormData.email || ""}
                        placeholder=" "
                        disabled
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      />
                      <label className="form-label">
                        Email <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${editFormData.primaryPhone ? "has-value" : ""} ${editValidationErrors.primaryPhone ? "form-group--error" : ""}`}
                    >
                      <input
                        type="tel"
                        className={`form-input ${editValidationErrors.primaryPhone ? "form-input--error" : ""}`}
                        value={editFormData.primaryPhone || ""}
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            primaryPhone: sanitizePhoneNumberInput(
                              e.target.value,
                            ),
                          });
                          if (editValidationErrors.primaryPhone) {
                            setEditValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.primaryPhone;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Primary Phone <span className="required">*</span>
                      </label>
                      {editValidationErrors.primaryPhone && (
                        <div className="field-error">
                          {editValidationErrors.primaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${editFormData.secondaryPhone ? "has-value" : ""} ${editValidationErrors.secondaryPhone ? "form-group--error" : ""}`}
                    >
                      <input
                        type="tel"
                        className={`form-input ${editValidationErrors.secondaryPhone ? "form-input--error" : ""}`}
                        value={editFormData.secondaryPhone || ""}
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            secondaryPhone: sanitizePhoneNumberInput(
                              e.target.value,
                            ),
                          });
                          if (editValidationErrors.secondaryPhone) {
                            setEditValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.secondaryPhone;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                      />
                      <label className="form-label">Secondary Phone</label>
                      {editValidationErrors.secondaryPhone && (
                        <div className="field-error">
                          {editValidationErrors.secondaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${editFormData.nationalIdNumber ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editFormData.nationalIdNumber || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            nationalIdNumber: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">National ID</label>
                    </div>

                    <div
                      className={`form-group ${editFormData.dateOfBirth ? "has-value" : ""} ${editValidationErrors.dateOfBirth ? "form-group--error" : ""}`}
                    >
                      <input
                        type="date"
                        className={`form-input ${editValidationErrors.dateOfBirth ? "form-input--error" : ""}`}
                        value={editFormData.dateOfBirth || ""}
                        max={maxDateOfBirth}
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            dateOfBirth: e.target.value,
                          });
                          if (editValidationErrors.dateOfBirth) {
                            setEditValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.dateOfBirth;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Date of Birth <span className="required">*</span>
                      </label>
                      {editValidationErrors.dateOfBirth && (
                        <div className="field-error">
                          {editValidationErrors.dateOfBirth}
                        </div>
                      )}
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.genderId || 1}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            genderId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                      >
                        <option value={1}>Male</option>
                        <option value={2}>Female</option>
                      </select>
                      <label className="form-label">
                        Gender <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${editFormData.roleId ? "has-value" : ""} ${editValidationErrors.roleId ? "form-group--error" : ""}`}
                    >
                      <select
                        className={`form-input ${editValidationErrors.roleId ? "form-input--error" : ""}`}
                        value={editFormData.roleId || ""}
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            roleId: parseInt(e.target.value, 10),
                          });
                          if (editValidationErrors.roleId) {
                            setEditValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.roleId;
                              return newErrors;
                            });
                          }
                        }}
                        required
                      >
                        <option value="">Select Role</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">
                        Role <span className="required">*</span>
                      </label>
                      {editValidationErrors.roleId && (
                        <div className="field-error">
                          {editValidationErrors.roleId}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowEditModal(false);
                        resetEditForm();
                      }}
                      disabled={editLoading}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={editLoading}
                    >
                      {editLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Updating...
                        </>
                      ) : (
                        "Update User"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Users;
